import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Inbox, ClipboardList, UserMinus, XCircle, RotateCcw, AlarmClock, UserX, CalendarOff } from "lucide-react";
import api from "@/lib/api";
import { useWebSocket, timeAgo } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Icône selon le type (lot 21) ; les anciennes notifications (type « mission ») prennent l'icône des missions.
// Lot T5b (transport) : alertes du dispatch (« pas encore reçue », « sans chauffeur ») et indisponibilité du chauffeur.
const ICONS = { booking_new: Inbox, mission_unassigned: UserMinus, mission_cancelled: XCircle, mission_reactivated: RotateCcw,
  ride_alert_not_received: AlarmClock, ride_alert_not_assigned: UserX, unavailability: CalendarOff };

// Cloche de la barre du haut : nombre de non lus, liste des 30 dernières, clic = marquer lu + ouvrir l'écran.
export default function NotificationBell() {
  const nav = useNavigate();
  const loc = useLocation();
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);

  const loadCount = useCallback(() => api.get("/notifications/unread-count").then((r) => setCount(r.data.count)).catch(() => {}), []);
  const loadList = useCallback(() => api.get("/notifications").then((r) => setItems(r.data)).catch(() => setItems((l) => l || [])), []);

  useEffect(() => {
    loadCount();
    const id = setInterval(loadCount, 12000);
    return () => clearInterval(id);
  }, [loc.pathname, loadCount]);

  // Signal du serveur au seul destinataire : la pastille (et la liste si elle est ouverte) se met à jour tout de suite.
  useWebSocket((msg) => {
    if (msg.type !== "notification") return;
    loadCount();
    if (open) loadList();
  });

  const onOpenChange = (o) => {
    setOpen(o);
    if (o) loadList();
  };

  const openItem = async (n) => {
    if (!n.read) {
      setItems((l) => l.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setCount((c) => Math.max(0, c - 1));
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      loadCount();
    }
    setOpen(false);
    nav(n.link || "/missions");
  };

  const markAll = async () => {
    setItems((l) => (l || []).map((x) => ({ ...x, read: true })));
    setCount(0);
    await api.post("/notifications/read").catch(() => {});
    loadCount();
  };

  const hasUnread = count > 0 || (items || []).some((n) => !n.read);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <div className="relative">
        <PopoverTrigger asChild>
          <Button variant="ghost" size="icon" data-testid="notif-bell" aria-label={count ? `Notifications : ${count} non ${count > 1 ? "lues" : "lue"}` : "Notifications"}>
            <Bell className="h-[18px] w-[18px]" />
          </Button>
        </PopoverTrigger>
        {count > 0 && (
          <span className="pointer-events-none absolute top-1 right-0.5 text-[10px] font-bold bg-accent text-accent-foreground rounded-full px-1 min-w-[16px] leading-4 text-center" data-testid="notif-count">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </div>
      <PopoverContent align="end" collisionPadding={16} className="w-[calc(100vw-2rem)] max-w-sm p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border">
          <div className="font-display font-semibold">Notifications</div>
          {hasUnread && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-muted-foreground" onClick={markAll} data-testid="notif-mark-all">
              Tout marquer comme lu
            </Button>
          )}
        </div>
        <div className="max-h-[70vh] overflow-y-auto">
          {items === null ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center">Chargement…</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="notif-empty">Aucune notification</div>
          ) : items.map((n) => {
            const Icon = ICONS[n.type] || ClipboardList;
            return (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`} data-unread={n.read ? "false" : "true"}
                className={cn("w-full flex items-start gap-3 px-4 py-3 text-left border-b border-border last:border-b-0 transition-colors hover:bg-muted",
                  !n.read && "bg-accent/10")}>
                <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", n.read ? "text-muted-foreground" : "text-accent")} strokeWidth={1.75}
                  data-testid={`notif-icon-${n.type}`} />
                <div className="min-w-0 flex-1">
                  <div className={cn("text-sm break-words", n.read ? "text-muted-foreground" : "font-medium text-foreground")}>{n.text}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{timeAgo(n.created_at)}</div>
                </div>
                {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-accent shrink-0" aria-label="Non lue" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
